// ProjectDecisions.js
import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  Divider,
  Collapse,
  Tooltip,
} from "@mui/material";

import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import Inventory2Rounded from "@mui/icons-material/Inventory2Rounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import MailRounded from "@mui/icons-material/MailRounded";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import UndoRounded from "@mui/icons-material/UndoRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SkipPreviousRounded from "@mui/icons-material/SkipPreviousRounded";
import { resolveDecision } from "../../Controllers/decisionController";
import Section from "./section";

const fmtLocal = (value, fmt = "DD.MM.YYYY HH:mm:ss") =>
  require("moment").utc(value, "YYYY-MM-DDTHH:mm:ssZ").local().format(fmt);

/**
 * Polished decisions panel with clearer hierarchy, counts, and smoother UX.
 * - Keeps renderDecision(decision) for custom item rendering
 * - One-pass filtering/sorting via useMemo
 * - Collapsible resolved section with accurate counts
 */
const ProjectDecisions = ({
  decisions = [],
  showResolved,
  setShowResolved,
  uid,
  user,
  setDecisions,
  setAlert,
  setError,
}) => {
  const { unresolved, resolved } = useMemo(() => {
    const byDateDesc = (a, b) => new Date(b.date) - new Date(a.date);
    const unresolved = decisions
      .filter((d) => d.resolved === null)
      .sort(byDateDesc);
    const resolved = decisions
      .filter((d) => d.resolved !== null)
      .sort(byDateDesc);
    return { unresolved, resolved };
  }, [decisions]);

  const unresolvedCount = unresolved.length;
  const resolvedCount = resolved.length;

  return (
    <Section
      title="DECISIONS AND COMMENTS"
      spacing={0}
      chips={[
        (i) => (
          <Box key={i} sx={{ pt: { xs: 0, sm: 1 }, pb: { xs: 1, sm: 0 } }}>
            <Chip
              size="small"
              color={unresolvedCount > 0 ? "warning" : "success"}
              variant={unresolvedCount > 0 ? "filled" : "outlined"}
              icon={
                unresolvedCount > 0 ? (
                  <HourglassBottomRounded />
                ) : (
                  <CheckCircleRounded />
                )
              }
              label={`${unresolvedCount} require${
                unresolvedCount === 1 ? "s" : ""
              } attention`}
            />
          </Box>
        ),
        (i) => (
          <Box key={i} sx={{ pt: { xs: 0, sm: 1 }, pb: { xs: 1, sm: 0 } }}>
            <Chip
              size="small"
              variant="outlined"
              label={`${resolvedCount} OK`}
            />
          </Box>
        ),
      ]}
      buttons={[
        (i) => (
          <Button
            key={i}
            variant={showResolved ? "contained" : "outlined"}
            size="small"
            onClick={() => setShowResolved((prev) => !prev)}
            disabled={resolvedCount === 0}
            startIcon={<Inventory2Rounded />}
            sx={{ borderRadius: 2 }}
          >
            {showResolved ? "Hide resolved" : `Show all (${resolvedCount})`}
          </Button>
        ),
      ]}
    >
      <Box>
        {/* Unresolved list */}
        {unresolvedCount !== 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1.25}>
              {unresolved.map((decision) => (
                <Box key={decision.id || decision.date}>
                  {renderDecision(
                    decision,
                    uid,
                    setDecisions,
                    setAlert,
                    setError,
                  )}
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* Resolved list (collapsible) */}
        <Collapse in={showResolved && resolvedCount > 0} unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Resolved ({resolvedCount})
          </Typography>
          <Stack spacing={1}>
            {resolved.map((decision) => (
              <Box key={`r-${decision.id || decision.date}`}>
                {renderDecision(
                  decision,
                  uid,
                  setDecisions,
                  setAlert,
                  setError,
                )}
              </Box>
            ))}
          </Stack>
        </Collapse>
      </Box>
    </Section>
  );
};

const renderDecision = (decision, uid, setDecisions, setAlert, setError) => {
  const isResolved = decision.resolved !== null;

  return (
    <Paper
      key={decision.decision_id}
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) =>
          isResolved ? t.palette.action.focus : t.palette.action.hover,
      }}
    >
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ minWidth: 0 }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ width: "70vw" }}
            alignItems="center"
          >
            {decision.decision === "Returned for Review" && (
              <SkipPreviousRounded fontSize="medium" />
            )}
            {(decision.decision === "Confirmed" ||
              decision.decision === "Approved") && (
              <CheckRounded fontSize="medium" />
            )}
            {decision.decision === "Rejected" && (
              <CloseRounded fontSize="medium" />
            )}

            <Typography variant="subtitle1">
              Project {decision.decision}
            </Typography>
          </Stack>
          <Tooltip
            title={isResolved ? "Mark as unresolved" : "Mark as resolved"}
          >
            <Button
              variant={isResolved ? "outlined" : "contained"}
              size="small"
              onClick={() =>
                resolveDecision(
                  decision.decision_id,
                  !isResolved,
                  uid,
                  setDecisions,
                  setAlert,
                  setError,
                )
              }
              startIcon={isResolved ? <UndoRounded /> : <CheckCircleRounded />}
              sx={{
                borderRadius: 2,
                backgroundColor: isResolved ? "inherit" : "success.main",
              }}
            >
              {isResolved ? "requires attention" : "OK"}
            </Button>
          </Tooltip>
        </Stack>
        {/* Manager row */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "start", sm: "center" }}
          justifyContent="start"
          spacing={1}
        >
          <Stack
            direction="row"
            justifyContent="start"
            spacing={1}
            alignItems="center"
          >
            <PersonOutlineRounded fontSize="small" />
            <Chip
              size="small"
              color="primary"
              variant="filled"
              label={
                decision.decision_by_role === "accountant"
                  ? "accountant"
                  : "approver"
              }
            />
            <Typography variant="subtitle3" noWrap>
              {decision.decision_by_name}
            </Typography>
          </Stack>

          {decision.decision_by_email && (
            <Chip
              size="small"
              icon={<MailRounded />}
              label={decision.decision_by_email}
              variant="outlined"
              sx={{
                maxWidth: { xs: 180, sm: 240 },
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          )}
        </Stack>
        {/* Meta row */}
        <Stack
          direction="column"
          alignItems="start"
          spacing={1.25}
          flexWrap="wrap"
        >
          <Chip
            size="small"
            variant="outlined"
            label={`Date: ${fmtLocal(decision.date)}`}
          />
          {isResolved && (
            <Chip
              size="small"
              variant="outlined"
              label={`Resolved by ${decision.resolved_by_name} on ${fmtLocal(
                decision.resolved,
              )}`}
              sx={{
                height: "auto", // allow Chip to grow vertically
                "& .MuiChip-label": {
                  whiteSpace: "normal", // allow wrapping
                  overflow: "visible", // prevent truncation
                  textOverflow: "unset", // remove ellipsis
                  display: "block", // let label expand fully
                  paddingY: "2px", // optional spacing
                },
              }}
              icon={<CheckCircleRounded />}
            />
          )}
        </Stack>

        <Divider />

        {/* Comments */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Comment:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {decision.comments || "No comments provided."}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ProjectDecisions;
